import type { PotStatusSummaryDto } from '../api/dashboardApi';

export type ChartPotStatus =
  | 'Open'
  | 'Verifying'
  | 'Verified'
  | 'Submitted'
  | 'Approved'
  | 'Paid'
  | 'Rejected'
  | 'MissedSubmitDeadline';

/** Ordered list of all displayable statuses (excludes Submitting, Archived) */
export const ALL_DISPLAY_STATUSES: ChartPotStatus[] = [
  'Open',
  'Verifying',
  'Verified',
  'Submitted',
  'Approved',
  'Paid',
  'Rejected',
  'MissedSubmitDeadline',
];

export const STATUS_COLORS: Record<ChartPotStatus, string> = {
  Open: '#2196F3',
  Verifying: '#FF9800',
  Verified: '#FFC107',
  Submitted: '#78909C',
  Approved: '#4CAF50',
  Paid: '#9E9E9E',
  Rejected: '#F44336',
  MissedSubmitDeadline: '#D32F2F',
};

export const STATUS_LABELS: Record<ChartPotStatus, string> = {
  Open: 'Open',
  Verifying: 'Verifying',
  Verified: 'Verified',
  Submitted: 'Submitted',
  Approved: 'Approved',
  Paid: 'Paid',
  Rejected: 'Rejected',
  MissedSubmitDeadline: 'Missed Deadline',
};

const EXCLUDED_STATUSES = new Set(['Submitting', 'Archived']);

export function getStatusColor(status: string): string {
  return STATUS_COLORS[status as ChartPotStatus] ?? '#9E9E9E';
}

export function getStatusLabel(status: string): string {
  return STATUS_LABELS[status as ChartPotStatus] ?? status;
}

export function filterDisplayStatuses(
  statuses: PotStatusSummaryDto[]
): PotStatusSummaryDto[] {
  return statuses.filter(s => !EXCLUDED_STATUSES.has(s.status));
}

/**
 * Merges API response data with the full list of display statuses,
 * filling in zero-count entries for statuses not present in the data.
 * Returns entries in the canonical display order.
 */
export function buildFullStatusList(
  apiStatuses: PotStatusSummaryDto[]
): PotStatusSummaryDto[] {
  const dataMap = new Map(
    filterDisplayStatuses(apiStatuses).map(s => [s.status, s])
  );

  return ALL_DISPLAY_STATUSES.map(
    status => dataMap.get(status) ?? { status, count: 0, grossValue: 0 }
  );
}
